import { merge } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { mixpanel, bigQuery } from '@fiverr-private/obs';
import { MixpanelEnrichment, BiEnrichment } from '../components/Consultation/types';
import { BI_CONSTS } from './constants';
import {
    ClosePopUpClickPayload,
    ConsultationDateClick,
    ConsultationDrawerViewPayload,
    ConsultationHourClick,
    ConsultationPackageClickPayload,
    ContinueConsultationClick,
} from './types';
import { getConsultationPackageSummary, getTimesFormatter } from './index';

export const reportConsultationExists = (mixpanelEnrichment: MixpanelEnrichment) => {
    const { Origin: origin } = mixpanelEnrichment;

    mixpanel.track(`${origin} - Consultation - Button Exists`, mixpanelEnrichment);
};

export const reportBookConsultationClick = (biEnrichment: BiEnrichment, mixpanelEnrichment: MixpanelEnrichment) => {
    const { pageCtxId } = getContext();
    const { Origin: origin } = mixpanelEnrichment;
    const { pageName, elementName } = biEnrichment;

    reportBiEvent(biEnrichment, {
        type: 'book_consultation',
        group: 'buyer_request',
        event_source: `${elementName}_${pageName}`,
        action: {
            type: 'click',
        },
        feature: {
            name: 'book_consultation',
        },
        page: {
            name: pageName,
            ctx_id: pageCtxId,
        },
    });

    mixpanel.track(`${origin} - Consultation - Button Click`, mixpanelEnrichment);
};

export const reportConsultationPackageClick = (mixpanelEnrichment: MixpanelEnrichment) => {
    const { Origin: origin } = mixpanelEnrichment;

    mixpanel.track(`${origin} - Consultation - Select Package`, mixpanelEnrichment);
};

export const reportConsultationDateClick = (mixpanelEnrichment: MixpanelEnrichment) => {
    const { Origin: origin } = mixpanelEnrichment;

    mixpanel.track(`${origin} - Consultation - Select Date`, mixpanelEnrichment);
};

export const reportConsultationTimeClick = (mixpanelEnrichment: MixpanelEnrichment) => {
    const { Origin: origin } = mixpanelEnrichment;

    mixpanel.track(`${origin} - Consultation - Select Time`, mixpanelEnrichment);
};

export const reportConsultationContinueClick = (mixpanelEnrichment: MixpanelEnrichment) => {
    const { Origin: origin } = mixpanelEnrichment;

    mixpanel.track(`${origin} - Consultation - Clicked Continue Order`, mixpanelEnrichment);
};

export const bookConsultationButtonView = (biEnrichment: BiEnrichment, sellerId: number) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId } = getContext();

    reportBiEvent(biEnrichment, {
        group: `${elementName}_book_consultation_impression`,
        type: `${pageName}.show$viewport`,
        seller: { id: sellerId },
        page: {
            component: { name: elementName },
            element: {
                type: 'button',
                text: 'Book a consultation',
                metadata: 'book consultation',
                name: `${elementName}_book_consultation`,
                visible: true,
            },
            ctx_id: pageCtxId,
        },
    });
};

export const consultationOnboardingView = (biEnrichment: BiEnrichment, sellerId: number) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { IMPRESSIONS },
    } = BI_CONSTS;

    reportBiEvent(biEnrichment, {
        group: IMPRESSIONS,
        type: 'book_a_consultation_pop_up_is_shown',
        event_source: pageName,
        seller: { id: sellerId },
        user: { id: userId },
        page: {
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
        },
    });
};

export const consultationIndicationView = (biEnrichment: BiEnrichment, sellerId: number) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { IMPRESSIONS },
    } = BI_CONSTS;

    reportBiEvent(biEnrichment, {
        group: IMPRESSIONS,
        type: 'offer_paid_consultations_badge_is_shown',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: pageName,
        page: {
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
        },
    });
};

export const consultationGetStartedClick = (biEnrichment: BiEnrichment, sellerId: number) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_book_a_consultation_get_started',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: pageName,
        page: {
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
        },
    });
};

export const closePopUpClick = (biEnrichment: BiEnrichment, { sellerId, popupSource }: ClosePopUpClickPayload) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_x_button',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: pageName,
        page: {
            element: { name: popupSource, text: 'x' },
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
        },
    });
};

export const learnMoreClick = (biEnrichment: BiEnrichment, sellerId: number) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_learn_more',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: pageName,
        page: {
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
        },
    });
};

export const consultationDrawerView = (
    biEnrichment: BiEnrichment,
    { sellerId, packages }: ConsultationDrawerViewPayload
) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { IMPRESSIONS },
    } = BI_CONSTS;

    const consultationPackagesSummary = packages
        .map((consultationPackage) => getConsultationPackageSummary(consultationPackage))
        .join();
    reportBiEvent(biEnrichment, {
        group: IMPRESSIONS,
        type: 'book_a_consultation_drawer_is_shown',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: pageName,
        page: {
            name: pageName,
            component: { name: elementName, num_of_elements: packages?.length },
            element: { text: consultationPackagesSummary },
            ctx_id: pageCtxId,
        },
    });
};

export const consultationPackageClick = (
    biEnrichment: BiEnrichment,
    { sellerId, selectedPackage, chosenDate }: ConsultationPackageClickPayload
) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    const readableDateString = chosenDate.toLocaleDateString('en-US');

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_book_a_consultation_meeting_length',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: 'book_a_consultation drawer',
        page: {
            name: pageName,
            component: { name: elementName, metadata: readableDateString },
            ctx_id: pageCtxId,
            element: {
                text: getConsultationPackageSummary(selectedPackage),
            },
        },
    });
};

export const continueConsultationClick = (
    biEnrichment: BiEnrichment,
    { sellerId, packagePrice }: ContinueConsultationClick
) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_book_a_consultation_continue',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: 'book_a_consultation drawer',
        page: {
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
            element: {
                text: packagePrice,
            },
        },
    });
};

export const consultationDateClick = (
    biEnrichment: BiEnrichment,
    { sellerId, timeFrames, chosenDate, packageNumber }: ConsultationDateClick
) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    const readableDateString = chosenDate.toLocaleDateString('en-US');

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_book_a_consultation_date',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: 'book_a_consultation drawer',
        component: { name: elementName, num_of_elements: timeFrames },
        page: {
            name: pageName,
            component: { name: elementName, num_of_elements: packageNumber },
            ctx_id: pageCtxId,
            element: {
                text: readableDateString,
            },
        },
    });
};

export const consultationHourClick = (biEnrichment: BiEnrichment, { sellerId, timeFrame }: ConsultationHourClick) => {
    const { pageName, elementName } = biEnrichment;
    const { pageCtxId, userId } = getContext();
    const {
        GROUP: { ACTION },
    } = BI_CONSTS;

    const timeFrameString = getTimesFormatter().format(timeFrame);

    reportBiEvent(biEnrichment, {
        group: ACTION,
        type: 'clicked_on_book_a_consultation_time',
        seller: { id: sellerId },
        user: { id: userId },
        event_source: 'book_a_consultation drawer',
        page: {
            name: pageName,
            component: { name: elementName },
            ctx_id: pageCtxId,
            element: {
                text: timeFrameString,
            },
        },
    });
};

const reportBiEvent = (biEnrichment, addedParams) => {
    const params = merge({}, biEnrichment, addedParams);

    bigQuery.send(params);
};
